import { catchError, finalize, map, Observable, throwError } from "rxjs";

/**
 * Interfaces
 */
import { IDataTableRequest } from "../interfaces";
import { IDataTableResponseModel } from "./interfaces";

/**
 * enums
 */
import { DataTableSortEnum } from "../enums";

/**
 * Services
 */
import { TableStateService } from "@sharedModules/tables";
import { AppHttpErrorResponse } from "@network";

/**
 * @class DataTableSourceMiddlewareModel
 * @description Middleware model for data table
 * @param state - The state of the data table
 */
export class DataTableSourceMiddlewareModel<TData> {

    /**
     * Constructor
     */
    constructor(
        private readonly _state: TableStateService,
        private readonly _dataSource: (bodyRequest: IDataTableRequest) => Observable<IDataTableResponseModel<TData>>
    ) {

        // validate state
        if (this._state == null) {
            throw new Error('State is required');
        }

        // validate state
        if (!(this._state instanceof TableStateService)) {
            throw new Error('State must be an instance of TableStateService');
        }

    }

    /**
     * Build the request body from the current table state.
     *
     * @param override - Fields to force onto the request, applied last
     * @returns The request body
     */
    public parseBodyRequest(override: Partial<IDataTableRequest> = {}): IDataTableRequest {
        return {
            ...DataTableSourceMiddlewareModel.defaultBodyRequest,
            page: this._state.currentPage() - 1,
            page_size: this._state.rowsPerPage(),
            // the debounced/normalized signal — the same value the table reloads
            // on, so the request can never disagree with what triggered it
            search_text: this._state.searchValueWithOperatorsPipe(),
            // search_list: this._state.searchList(),
            ...this._parseSortRequest(),
            ...override,
        };
    }

    /**
     * Translate the table's sort state into the API's sort fields.
     *
     * The table sorts in its own vocabulary — a view-model field name and a
     * lowercase order — so both halves are converted here: `sortColumn()` has
     * already resolved the column's `sortField` (its API name), and the order
     * is mapped onto {@link DataTableSortEnum}.
     *
     * Only the primary rule travels: the API sorts by a single column. An
     * unsorted table falls back to {@link defaultBodyRequest}, which sends an
     * empty column and lets the API apply its own ordering.
     *
     * @returns The sort fields of the request body
     */
    private _parseSortRequest(): Pick<IDataTableRequest, 'sort_column' | 'sort_type'> {

        const column = this._state.sortColumn();
        const order = this._state.sortOrder();

        // nothing sorted — keep the defaults
        if (column.length === 0 || order == null) {
            return {
                sort_column: DataTableSourceMiddlewareModel.defaultBodyRequest.sort_column,
                sort_type: DataTableSourceMiddlewareModel.defaultBodyRequest.sort_type,
            };
        }

        return {
            sort_column: column,
            sort_type: order === 'desc' ? DataTableSortEnum.DESC : DataTableSortEnum.ASC,
        };
    }

    /**
     * Run the data table source middleware
     *
     * Reads through {@link IDataTableResponseModel}, whose getters already
     * guarantee an array of rows and numeric counts — so no defensive
     * checks are needed here.
     *
     * The error state belongs to the load that set it: a new load clears the
     * previous error (`''` is the state's "no error") before it starts, so a
     * retry that succeeds shows its rows, and one that fails shows its own
     * error.
     *
     * @returns Observable<IDataTableResponseModel<TData>>
     */
    public loadData(): Observable<IDataTableResponseModel<TData>> {

        // set loading, and clear the previous load's error
        this._state.setLoading(true);
        this._state.setError('');

        return this._dataSource(this.parseBodyRequest()).pipe(
            map((response) => {

                this._state.loadData(response.data, response.paginationCount);

                return response;
            }),
            catchError((error: AppHttpErrorResponse) => {
                this._state.setError(error.singleErrorMessage ?? error.message);
                return throwError(() => error);
            }),
            finalize(() => {
                this._state.setLoading(false);
            }),
        );

    }

    /**
     * Default body request
     */
    public static defaultBodyRequest: IDataTableRequest = {
        page: 0,
        page_size: 10,
        search_text: "",
        search_list: [],
        sort_column: "",
        sort_type: DataTableSortEnum.ASC,
    };

}
